/**
 * Non-blocking first-party product telemetry transport.
 *
 * Events are batched, authenticated, and scoped to a browser-tab session.
 * No raw input values are accepted by this API.
 */

import type {
  HoursReportingFailure,
  ObservabilityOverview,
  RealtimeMetrics,
  TelemetryEventType,
  TelemetryMetadata,
  TelemetryOverview,
  TelemetrySessionDetail,
  TelemetrySessionListResponse,
} from "@/types";
import { useAuthStore } from "@/stores/authStore";
import api from "./api";
import {
  normalizeTelemetryPagePath,
  sanitizeTelemetryLabel,
} from "@/utils/telemetry";
import {
  advanceTelemetrySequence,
  beginWorkflowAttempt,
  clearWorkflowAttempt,
  getActiveWorkflowAttemptId,
  getClientVersion,
} from "@/utils/telemetryContext";
import { ANALYTICS_OPERATIONS, ANALYTICS_WORKFLOWS } from "@/constants/analyticsOperations";

const BASE_URL = import.meta.env.VITE_API_URL ?? "";
const TELEMETRY_ENABLED = import.meta.env.VITE_TELEMETRY_ENABLED !== "false";
const MAX_QUEUE_SIZE = 100;
const BATCH_SIZE = 25;
const FLUSH_DELAY_MS = 1_500;
const DEDUPLICATION_MS = 500;
const RETRY_DELAY_MS = 5_000;
const MAX_RETRY_DELAY_MS = 60_000;
const MAX_TIMER_DELAY_MS = 2_147_483_647;

interface QueuedTelemetryEvent {
  sessionId: string;
  sequence: number;
  eventType: TelemetryEventType;
  pagePath: string;
  target?: string;
  metadata?: TelemetryMetadata;
  clientTimestamp: string;
}

export interface TrackTelemetryEvent {
  eventType: TelemetryEventType;
  pagePath?: string;
  target?: string;
  metadata?: TelemetryMetadata;
}

export type HoursReportingStep =
  | "opened"
  | "extra_work_opened"
  | "extra_work_cancelled"
  | "extra_work_confirmed"
  | "submission_started"
  | "succeeded"
  | "failed";

export type HoursReportingSource = "cleaner_dashboard" | "orders_list" | "order_detail";

export interface HoursReportingContext {
  source: HoursReportingSource;
  orderType?: "GU" | "PU";
  hasExtraWork?: boolean;
  usedCustomHours?: boolean;
  errorCategory?: string;
}

let queue: QueuedTelemetryEvent[] = [];
let retryBatch: QueuedTelemetryEvent[] | null = null;
let retryBatchAuthRetried = false;
let queueUserId = authenticatedUserId(useAuthStore.getState());
let authGeneration = 0;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let isFlushing = false;
let retryNotBefore = 0;
let retryDelay = RETRY_DELAY_MS;
const recentEvents = new Map<string, number>();

function authenticatedUserId(auth: ReturnType<typeof useAuthStore.getState>): string | null {
  return auth.isAuthenticated && auth.token ? auth.user?.id ?? null : null;
}

// Observe logout even if the same user logs back in before the next event or response.
useAuthStore.subscribe((auth) => {
  const userId = authenticatedUserId(auth);
  if (queueUserId === userId) return;
  authGeneration += 1;
  queueUserId = userId;
  queue = [];
  retryBatch = null;
  retryBatchAuthRetried = false;
  retryNotBefore = 0;
  retryDelay = RETRY_DELAY_MS;
  recentEvents.clear();
  clearFlushTimer();
});

function clearFlushTimer(): void {
  if (flushTimer !== null) clearTimeout(flushTimer);
  flushTimer = null;
}

function scheduleFlush(delay = FLUSH_DELAY_MS): void {
  if (flushTimer !== null || isFlushing) return;
  const wait = Math.max(delay, retryNotBefore - Date.now(), 0);
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushTelemetry();
  }, Math.min(wait, MAX_TIMER_DELAY_MS));
}

function retryAfterDelay(header: string | null): number | null {
  if (!header?.trim()) return null;
  const value = header.trim();
  const delay = /^(?:\d+(?:\.\d+)?|\.\d+)$/.test(value)
    ? Number(value) * 1_000
    : Date.parse(value) - Date.now();
  // Zero/past dates use backoff too, rather than repeatedly retrying immediately.
  return Number.isFinite(delay) && delay > 0 ? Math.ceil(delay) : null;
}

export function trackTelemetry(event: TrackTelemetryEvent): void {
  if (!TELEMETRY_ENABLED || typeof window === "undefined") return;

  const auth = useAuthStore.getState();
  if (!auth.isAuthenticated || !auth.user?.id || !auth.token) return;

  const pagePath = normalizeTelemetryPagePath(
    event.pagePath ?? `${window.location.pathname}${window.location.search}`,
  );
  const target = event.target ? sanitizeTelemetryLabel(event.target) : undefined;
  const fingerprint = `${event.eventType}|${pagePath}|${target ?? ""}`;
  const now = Date.now();
  const lastSeen = recentEvents.get(fingerprint);
  if (lastSeen !== undefined && now - lastSeen < DEDUPLICATION_MS) return;
  recentEvents.set(fingerprint, now);

  const session = advanceTelemetrySequence(auth.user.id);
  queue.push({
    ...session,
    eventType: event.eventType,
    pagePath,
    target,
    metadata: { ...event.metadata, appVersion: getClientVersion() },
    clientTimestamp: new Date().toISOString(),
  });

  if (queue.length + (retryBatch?.length ?? 0) > MAX_QUEUE_SIZE) {
    // Keep the attempted batch intact, preferring workflow evidence over UI noise.
    const expendable = queue.findIndex(
      ({ eventType }) => eventType !== "workflow_step" && eventType !== "form_submit",
    );
    queue.splice(Math.max(0, expendable), 1);
  }
  scheduleFlush(queue.length >= 10 ? 0 : FLUSH_DELAY_MS);
}

export function trackHoursReportingStep(
  step: HoursReportingStep,
  context: HoursReportingContext,
): void {
  if ((step === "opened" || step === "submission_started") && !getActiveWorkflowAttemptId()) {
    beginWorkflowAttempt("hours_reporting");
  }
  const workflowAttemptId = getActiveWorkflowAttemptId() ?? beginWorkflowAttempt("hours_reporting");
  trackTelemetry({
    eventType: "workflow_step",
    target: ANALYTICS_OPERATIONS.hoursReporting[
      step === "extra_work_opened" ? "extraWorkOpened"
        : step === "extra_work_cancelled" ? "extraWorkCancelled"
          : step === "extra_work_confirmed" ? "extraWorkConfirmed"
            : step === "submission_started" ? "submissionStarted"
              : step
    ],
    metadata: {
      workflowName: ANALYTICS_WORKFLOWS.hoursReporting,
      workflowStep: step,
      workflowSource: context.source,
      workflowAttemptId,
      ...(context.orderType ? { orderType: context.orderType } : {}),
      ...(context.hasExtraWork !== undefined ? { hasExtraWork: context.hasExtraWork } : {}),
      ...(context.usedCustomHours !== undefined ? { usedCustomHours: context.usedCustomHours } : {}),
      ...(context.errorCategory ? { errorCategory: context.errorCategory } : {}),
      outcome: step === "succeeded" ? "success" : step === "failed" ? "error" : "progress",
    },
  });
  if (["succeeded", "failed", "extra_work_cancelled"].includes(step)) clearWorkflowAttempt();
}

/** Flush queued events. `keepalive` is used when the page is being hidden. */
export async function flushTelemetry(keepalive = false): Promise<void> {
  if (!TELEMETRY_ENABLED || isFlushing || (queue.length === 0 && !retryBatch)) return;
  if (Date.now() < retryNotBefore) {
    scheduleFlush(0);
    return;
  }
  clearFlushTimer();

  const auth = useAuthStore.getState();
  if (!auth.token || authenticatedUserId(auth) !== queueUserId || !queueUserId) return;

  const generation = authGeneration;
  const token = auth.token;
  if (!retryBatch) {
    retryBatch = queue.splice(0, BATCH_SIZE);
    retryBatchAuthRetried = false;
  }
  const body = JSON.stringify({ events: retryBatch });
  isFlushing = true;
  try {
    const response = await fetch(`${BASE_URL}/api/telemetry/events`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      credentials: "include",
      keepalive,
      body,
    }).catch(() => null);

    if (generation !== authGeneration) return;
    if (response?.status === 401 && !retryBatchAuthRetried) {
      // An expired access token must not silently drop the batch: retry it once
      // with a token another request already refreshed, or refresh it here.
      retryBatchAuthRetried = true;
      const currentToken = useAuthStore.getState().token;
      const canRetry = Boolean(currentToken && currentToken !== token)
        || await useAuthStore.getState().refreshAuth();
      if (generation !== authGeneration) return;
      if (!canRetry) retryBatch = null;
    } else if (!response || response.status === 429 || response.status >= 500) {
      const delay = response?.status === 429
        ? retryAfterDelay(response.headers.get("Retry-After")) ?? retryDelay
        : retryDelay;
      retryNotBefore = Date.now() + delay;
      retryDelay = Math.min(retryDelay * 2, MAX_RETRY_DELAY_MS);
    } else {
      retryBatch = null;
      retryNotBefore = 0;
      retryDelay = RETRY_DELAY_MS;
    }
  } finally {
    isFlushing = false;
    if (retryBatch || queue.length > 0) {
      scheduleFlush(retryBatch || keepalive ? 0 : FLUSH_DELAY_MS);
    }
  }
}

// Admin analytics API -------------------------------------------------------

export function getTelemetryOverview(days: number): Promise<TelemetryOverview> {
  return api.get<TelemetryOverview>(`/api/telemetry/overview?days=${days}`);
}

export function getObservabilityOverview(days: number): Promise<ObservabilityOverview> {
  return api.get<ObservabilityOverview>(`/api/observability/overview?days=${days}`);
}

export function getRealtimeMetrics(): Promise<RealtimeMetrics> {
  return api.get<RealtimeMetrics>("/api/observability/realtime");
}

export function resolveObservabilityAlert(id: string): Promise<{ resolved: boolean }> {
  return api.patch<{ resolved: boolean }>(`/api/observability/alerts/${encodeURIComponent(id)}/resolve`);
}

export function getHoursReportingFailures(days: number): Promise<{ data: HoursReportingFailure[] }> {
  return api.get<{ data: HoursReportingFailure[] }>(
    `/api/telemetry/hours-reporting/failures?days=${days}`,
  );
}

export function getTelemetrySessions(
  days: number,
  page: number,
  limit: number,
  workflow?: string,
): Promise<TelemetrySessionListResponse> {
  const workflowQuery = workflow ? `&workflow=${encodeURIComponent(workflow)}` : "";
  return api.get<TelemetrySessionListResponse>(
    `/api/telemetry/sessions?days=${days}&page=${page}&limit=${limit}${workflowQuery}`,
  );
}

export function getTelemetrySession(sessionId: string): Promise<TelemetrySessionDetail> {
  return api.get<TelemetrySessionDetail>(`/api/telemetry/sessions/${encodeURIComponent(sessionId)}`);
}
